/**
 * Hierarchical topology scene — Corporate Park.
 *
 * Campus layout:
 *   - Root building (5x5 manager) at top, centered
 *   - Each BFS level is a row of buildings below
 *   - Stone paths from parent buildings to child buildings
 *
 * Scales: deeper tree = more rows, wider levels = wider rows.
 */

import type { SceneLayout, TopologyInfo, BuildingFootprint, CampusDef } from "./SceneLayout.js";
import { campusToLayout, routePath } from "./SceneLayout.js";
import { getFurnitureForBuilding } from "./FurniturePresets.js";

const ROOT_SIZE = 6;
const NODE_SIZE = 4;
const GAP = 4;

/** Build levels by BFS from entry point. */
function buildLevels(topo: TopologyInfo): Map<string, number> {
  const levels = new Map<string, number>();
  const entry = topo.entryPoint ?? topo.agents[0]?.id;
  if (!entry) return levels;

  const queue = [entry];
  levels.set(entry, 0);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const level = levels.get(current)!;
    for (const edge of topo.edges) {
      if (edge.from === current && !levels.has(edge.to)) {
        levels.set(edge.to, level + 1);
        queue.push(edge.to);
      }
    }
  }

  // Anything unreached goes to level 1
  for (const a of topo.agents) {
    if (!levels.has(a.id)) levels.set(a.id, 1);
  }

  return levels;
}

export function hierarchicalLayout(topo: TopologyInfo): SceneLayout {
  const levels = buildLevels(topo);
  const buildings: BuildingFootprint[] = [];

  // Group agents by level
  const byLevel = new Map<number, string[]>();
  for (const [id, level] of levels) {
    if (!byLevel.has(level)) byLevel.set(level, []);
    byLevel.get(level)!.push(id);
  }

  const maxLevel = Math.max(...byLevel.keys(), 0);

  // Map agent IDs to buildings for path routing
  const buildingMap = new Map<string, BuildingFootprint>();

  for (let level = 0; level <= maxLevel; level++) {
    const agentIds = byLevel.get(level) ?? [];
    const count = agentIds.length;
    const isRoot = level === 0;
    const size = isRoot ? ROOT_SIZE : NODE_SIZE;

    // Center this level's buildings horizontally
    const totalWidth = count * size + (count - 1) * GAP;
    const startCol = -Math.floor(totalWidth / 2);
    const levelRow = level * (size + GAP);

    for (let i = 0; i < count; i++) {
      const bCol = startCol + i * (size + GAP);
      const b: BuildingFootprint = {
        id: `level-${level}-${i}`,
        col: bCol,
        row: levelRow,
        width: size,
        height: size,
        floorType: isRoot ? "manager" : "office",
        agents: [{
          agentId: agentIds[i],
          localCol: Math.floor(size / 2),
          localRow: Math.floor(size / 2),
          deskType: isRoot ? "large" : "small",
          isEntryPoint: agentIds[i] === topo.entryPoint,
        }],
        furniture: getFurnitureForBuilding(
          isRoot ? "entry" : "worker",
          "park",
          bCol, levelRow, size, size,
        ),
      };
      buildings.push(b);
      buildingMap.set(agentIds[i], b);
    }
  }

  // Route paths from parents to children
  const pathCells: Array<{ col: number; row: number }> = [];
  for (const edge of topo.edges) {
    const fromB = buildingMap.get(edge.from);
    const toB = buildingMap.get(edge.to);
    if (fromB && toB && fromB !== toB) {
      pathCells.push(...routePath(fromB, toB));
    }
  }

  const campus: CampusDef = {
    buildings,
    pathCells,
    decorations: [],
  };

  return campusToLayout(campus);
}
